import api from '@/lib/api'
import type { Model, ModelVersion } from '@/types'
import { useCasesService, type UseCaseInfo } from './useCases'
import { type ModelId, type VersionId, unbrand } from '@/types/branded'
import type { APIErrorResponse } from '@/types/errors'
import { extractAPIError } from '@/lib/error-utils'

// Re-export UseCaseInfo for backward compatibility
export type { UseCaseInfo }

// Re-export training error utilities for use in components
export { parseTrainingError, type TrainingErrorInfo } from '@/lib/trainingErrors'

/**
 * Typed result wrapper for service operations.
 * Enables discriminated union pattern for error handling.
 */
export type Result<T, E = APIErrorResponse> =
  | { success: true; data: T }
  | { success: false; error: E }

export const modelsService = {
  /**
   * List use cases - delegates to useCasesService for consistency
   * @deprecated Use useCasesService.listUseCases() instead for better caching
   */
  async listUseCases(category?: string) {
    return useCasesService.listUseCases(category)
  },

  /**
   * List all models with optional filtering.
   */
  async list(params?: { is_active?: boolean; use_case?: string }): Promise<Result<{ models: Model[]; total: number }>> {
    try {
      const { data } = await api.get<{ models: Model[]; total: number }>('/models', { params })
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get a single model by ID.
   */
  async get(id: ModelId): Promise<Result<Model>> {
    try {
      const { data } = await api.get<Model>(`/models/${unbrand(id)}`)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Create a new model.
   */
  async create(model: Partial<Model> & { column_mappings?: Record<string, string> }): Promise<Result<Model>> {
    try {
      const { data } = await api.post<Model>('/models', model)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Update an existing model.
   */
  async update(id: ModelId, model: Partial<Model>): Promise<Result<Model>> {
    try {
      const { data } = await api.patch<Model>(`/models/${unbrand(id)}`, model)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Delete a model.
   */
  async delete(id: ModelId): Promise<Result<void>> {
    try {
      await api.delete(`/models/${unbrand(id)}`)
      return { success: true, data: undefined }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get all versions for a model.
   */
  async getVersions(id: ModelId): Promise<Result<{ versions: ModelVersion[]; total: number }>> {
    try {
      const { data } = await api.get<{ versions: ModelVersion[]; total: number }>(
        `/models/${unbrand(id)}/versions`
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Create a new model version.
   */
  async createVersion(
    id: ModelId,
    config?: { hyperparameters?: Record<string, unknown>; training_config?: Record<string, unknown> }
  ): Promise<Result<ModelVersion>> {
    try {
      const { data } = await api.post<ModelVersion>(`/models/${unbrand(id)}/versions`, config || {})
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Deploy a model version.
   */
  async deployVersion(
    id: ModelId,
    verId: VersionId,
    trafficPercentage: number = 100
  ): Promise<Result<unknown>> {
    try {
      const { data } = await api.post(`/models/${unbrand(id)}/versions/${unbrand(verId)}/deploy`, {
        traffic_percentage: trafficPercentage,
      })
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Cancel a training version.
   */
  async cancelVersion(id: ModelId, verId: VersionId): Promise<Result<unknown>> {
    try {
      const { data } = await api.post(`/models/${unbrand(id)}/versions/${unbrand(verId)}/cancel`)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get training progress for a model version.
   */
  async getTrainingProgress(id: ModelId, verId: VersionId): Promise<Result<{
    version_id: string
    status: string
    progress: number
    step: string
    estimate_seconds: number | null
    estimate_text: string | null
    n_rows: number | null
    n_features: number | null
    started_at: string | null
    updated_at: string | null
    error: string | null
    error_type: string | null
    error_traceback: string | null
    failed_at: string | null
    metrics: Record<string, number> | null
  }>> {
    try {
      const { data } = await api.get<{
        version_id: string
        status: string
        progress: number
        step: string
        estimate_seconds: number | null
        estimate_text: string | null
        n_rows: number | null
        n_features: number | null
        started_at: string | null
        updated_at: string | null
        error: string | null
        error_type: string | null
        error_traceback: string | null
        failed_at: string | null
        metrics: Record<string, number> | null
      }>(`/models/${unbrand(id)}/versions/${unbrand(verId)}/progress`)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Retry training for a failed version.
   */
  async retryTraining(id: ModelId, verId: VersionId): Promise<Result<{
    success: boolean
    job_id: string
    version_id: string
    message: string
  }>> {
    try {
      const { data } = await api.post<{
        success: boolean
        job_id: string
        version_id: string
        message: string
      }>(`/training/${unbrand(id)}/versions/${unbrand(verId)}/retry`)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Quick train with demo data (for onboarding).
   * @deprecated Demo data functionality may not be available
   */
  async quickTrain(params: {
    name: string
    use_case: string
    n_samples?: number
    churn_rate?: number
    auto_deploy?: boolean
  }): Promise<{ model_id: string }> {
    // This endpoint may not exist in the current backend
    // Used for demo/onboarding flows
    const { data } = await api.post<{ model_id: string }>('/models/quick-train', params)
    return data
  },
}
